import { useState } from 'react';
import { Modal } from '@/components/common/Modal';
import { useResolveError, useAffectedDevices } from '@/hooks/useErrors';
import {
  Flame,
  AlertCircle,
  AlertTriangle,
  Check,
  RefreshCw,
  Smartphone,
  User,
  Calendar,
  Code,
  Copy,
} from 'lucide-react';
import type { ErrorSeverity, AppError } from '@/types/error.types';
import { formatDateTime } from '@/utils/formatters';

interface ErrorDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  error: AppError;
}

const getSeverityIcon = (severity: ErrorSeverity) => {
  switch (severity) {
    case 'critical':
      return <Flame size={18} className="text-error" />;
    case 'high':
      return <AlertCircle size={18} className="text-warning" />;
    case 'medium':
      return <AlertTriangle size={18} className="text-amber-500" />;
    default:
      return null;
  }
};

const getSeverityLabel = (severity: ErrorSeverity) => {
  switch (severity) {
    case 'critical':
      return 'Crítico';
    case 'high':
      return 'Alto';
    case 'medium':
      return 'Médio';
    case 'low':
      return 'Baixo';
    default:
      return severity;
  }
};

export function ErrorDetailsModal({ isOpen, onClose, error }: ErrorDetailsModalProps) {
  const {
    data: affectedDevices,
    isLoading: isLoadingDevices,
    error: devicesError,
  } = useAffectedDevices(error.id);
  const resolveError = useResolveError();
  const [notes, setNotes] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleResolve = async () => {
    setIsResolving(true);
    try {
      await resolveError.mutateAsync({ id: error.id, notes: notes || undefined });
      onClose();
    } catch (err) {
      console.error('Failed to resolve error:', err);
      alert('Erro ao marcar como resolvido. Verifique se a API está rodando.');
    } finally {
      setIsResolving(false);
    }
  };

  const isResolved = error.status === 'resolved';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Detalhes do Erro" size="xl">
      <div className="p-6">
        {/* Header Info */}
        <div className="mb-6 flex items-start justify-between">
          <div className="flex w-full flex-col gap-3">
            <div className="flex items-center gap-2">
              {getSeverityIcon(error.severity)}
              <span className="text-lg font-semibold text-text-title">
                {getSeverityLabel(error.severity)}
              </span>
              {isResolved ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">
                  <Check size={14} />
                  Resolvido
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-error/10 px-3 py-1 text-xs font-medium text-error">
                  Não resolvido
                </span>
              )}
            </div>

            {/* Error Message in styled container */}
            <div className="relative overflow-hidden rounded-xl border border-border-strong bg-card shadow-sm">
              <div className="flex items-center justify-between bg-muted px-5 py-3">
                <div className="flex items-center gap-2">
                  <Code size={16} className="text-text-secondary" />
                  <span className="text-sm font-medium text-text-title">Detalhes do Erro</span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(error.message);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    } catch (err) {
                      console.error('Failed to copy error:', err);
                    }
                  }}
                  className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                    copied
                      ? 'bg-success text-white shadow-md'
                      : 'border border-border-strong bg-card text-text-secondary hover:bg-muted hover:text-text-title'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check size={14} />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      Copiar código
                    </>
                  )}
                </button>
              </div>
              <div className="overflow-x-auto bg-muted/40 p-6">
                <pre className="whitespace-pre-wrap break-words font-mono text-sm leading-relaxed text-text-title">
                  <code>{error.message}</code>
                </pre>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm text-text-secondary">
              <span className="flex items-center gap-1">
                <Calendar size={16} />
                Primeira ocorrência: {formatDateTime(error.firstOccurrence)}
              </span>
              <span className="flex items-center gap-1">
                <Calendar size={16} />
                Última ocorrência: {formatDateTime(error.lastOccurrence)}
              </span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-6 grid gap-4 grid-cols-3">
          <div className="rounded-lg border border-border bg-muted/50 p-4">
            <p className="text-sm font-medium text-text-secondary">Total de Ocorrências</p>
            <p className="mt-1 text-2xl font-bold text-text-title">
              {(error.occurrences || 0).toLocaleString()}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-muted/50 p-4">
            <p className="text-sm font-medium text-text-secondary">Dispositivos Afetados</p>
            <p className="mt-1 text-2xl font-bold text-text-title">{error.affectedDevices || 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-muted/50 p-4">
            <p className="text-sm font-medium text-text-secondary">Tipo</p>
            <p className="mt-1 text-2xl font-bold text-text-title capitalize">
              {error.type || 'N/A'}
            </p>
          </div>
        </div>

        {/* Versions */}
        {error.affectedVersions && error.affectedVersions.length > 0 && (
          <div className="mb-6">
            <h4 className="mb-2 text-sm font-semibold text-text-title">Versões Afetadas</h4>
            <div className="flex flex-wrap gap-2">
              {error.affectedVersions.map((version) => (
                <span
                  key={version}
                  className="rounded-lg bg-muted px-3 py-1 font-mono text-sm text-text-title"
                >
                  {version}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stack Trace */}
        {error.stackTrace && (
          <div className="mb-6">
            <div className="relative overflow-hidden rounded-xl border border-border-strong bg-card shadow-sm">
              {/* Header */}
              <div className="flex items-center justify-between bg-muted px-5 py-3">
                <div className="flex items-center gap-2">
                  <Code size={16} className="text-text-secondary" />
                  <span className="text-sm font-medium text-text-title">Stack Trace</span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(error.stackTrace || '');
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    } catch (err) {
                      console.error('Failed to copy stack trace:', err);
                    }
                  }}
                  className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                    copied
                      ? 'bg-success text-white shadow-md'
                      : 'border border-border-strong bg-card text-text-secondary hover:bg-muted hover:text-text-title'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check size={14} />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      Copiar código
                    </>
                  )}
                </button>
              </div>
              {/* Code Block */}
              <div className="overflow-x-auto bg-muted/40 p-6">
                <pre className="font-mono text-sm leading-relaxed text-text-title">
                  <code>{error.stackTrace}</code>
                </pre>
              </div>
            </div>
          </div>
        )}

        {/* Resolution Info */}
        {isResolved && (
          <div className="mb-6 rounded-lg border border-success/30 bg-success/10 p-4">
            <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-success">
              <Check size={16} />
              Informações de Resolução
            </h4>
            {error.resolvedAt && (
              <p className="text-sm text-text-secondary">
                Resolvido em: {formatDateTime(error.resolvedAt)}
              </p>
            )}
            {error.resolvedBy && (
              <p className="text-sm text-text-secondary">Resolvido por: {error.resolvedBy}</p>
            )}
            {error.notes && (
              <div className="mt-2">
                <p className="text-sm font-medium text-text-title">Notas:</p>
                <p className="text-sm text-text-secondary">{error.notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Resolve Form */}
        {!isResolved && (
          <div className="mb-6 rounded-lg border border-border bg-muted/50 p-4">
            <h4 className="mb-3 text-sm font-semibold text-text-title">Marcar como Resolvido</h4>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicione notas sobre a resolução (opcional)"
              className="mb-3 w-full rounded-lg border border-border-strong p-3 text-sm text-text-title placeholder:text-text-hint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              rows={3}
            />
            <button
              onClick={handleResolve}
              disabled={isResolving}
              className="flex items-center gap-2 rounded-lg bg-success px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-success/90 active:scale-[0.98] disabled:opacity-50"
            >
              {isResolving ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Resolvendo...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Marcar como Resolvido
                </>
              )}
            </button>
          </div>
        )}

        {/* Affected Devices/Users */}
        <div>
          <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-title">
            <Smartphone size={16} />
            Dispositivos e Usuários Afetados ({error.affectedDevices})
          </h4>
          <div className="rounded-lg border border-border">
            {isLoadingDevices ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="size-6 animate-spin text-primary" />
              </div>
            ) : devicesError ? (
              <div className="p-8 text-center text-sm text-text-secondary">
                <Smartphone size={32} className="mx-auto mb-2 opacity-50" />
                <p className="font-medium text-text-title">Endpoint não disponível</p>
                <p className="mt-1">
                  A API ainda não implementou o endpoint de dispositivos afetados.
                </p>
                <p className="mt-1 text-xs">
                  Total de dispositivos afetados: <strong>{error.affectedDevices}</strong>
                </p>
              </div>
            ) : affectedDevices && affectedDevices.length > 0 ? (
              <>
                <div className="border-b border-border bg-muted/50 px-4 py-3">
                  <div className="grid grid-cols-12 gap-4 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    <div className="col-span-3">Usuário</div>
                    <div className="col-span-3">Dispositivo</div>
                    <div className="col-span-2">Versão App</div>
                    <div className="col-span-2 text-center">Ocorrências</div>
                    <div className="col-span-2">Última Ocorrência</div>
                  </div>
                </div>
                <div className="divide-y divide-border">
                  {affectedDevices.map((device) => (
                    <div
                      key={device.deviceId}
                      className="grid grid-cols-12 items-center gap-4 px-4 py-3 text-sm transition-colors hover:bg-muted/50"
                    >
                      <div className="col-span-3 flex items-center gap-2">
                        <User size={16} className="text-text-secondary" />
                        <div className="flex flex-col">
                          <span className="font-medium text-text-title">{device.userName}</span>
                          <span className="text-xs text-text-secondary">{device.userId}</span>
                        </div>
                      </div>
                      <div className="col-span-3">
                        <div className="flex flex-col">
                          <span className="font-medium text-text-title">{device.deviceModel}</span>
                          <span className="text-xs text-text-secondary">
                            OS: {device.osVersion}
                          </span>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <span className="rounded-lg bg-muted px-2 py-1 font-mono text-xs text-text-title">
                          {device.appVersion}
                        </span>
                      </div>
                      <div className="col-span-2 text-center font-semibold text-text-title">
                        {device.occurrences}
                      </div>
                      <div className="col-span-2 text-xs text-text-secondary">
                        {formatDateTime(device.lastOccurrence)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="p-8 text-center text-sm text-text-secondary">
                <Smartphone size={32} className="mx-auto mb-2 opacity-50" />
                <p>Nenhum dispositivo afetado encontrado</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
